/**
 * Stay-points from the current run, on the mission map.
 *
 * The Dwells tab is the list. This is the same claim as a pin — where someone
 * actually lingered, not a threat. Own kit is teal; other activity on the
 * line during the same window is gold.
 */
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Marker } from 'react-map-gl';
import { RichTooltip } from '../../../components/common';
import { useMapLayerVisible } from '../../map/mapLayerVisibility';
import { MissionDwell, listMissionDwells } from '../../../services/missionApi';
import { formatSpan, formatWhen } from '../format';

const TEAL = '#00b8a9';
const GOLD = '#f5b942';

export interface MissionDwellLayerProps {
  missionId: number;
  started?: boolean;
  refreshKey?: number;
  interactive?: boolean;
  onSelect?: (latitude: number, longitude: number) => void;
}

const MissionDwellLayer: React.FC<MissionDwellLayerProps> = ({
  missionId,
  started = true,
  refreshKey,
  interactive = true,
  onSelect,
}) => {
  const [dwells, setDwells] = useState<MissionDwell[]>([]);
  const visible = useMapLayerVisible('dwells');

  useEffect(() => {
    if (!started) {
      setDwells([]);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      listMissionDwells(missionId)
        .then((rows) => { if (!cancelled) setDwells(rows); })
        .catch(() => { if (!cancelled) setDwells([]); });
    };
    load();
    const timer = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [missionId, started, refreshKey]);

  if (!visible) return null;

  return (
    <>
      {dwells.map((dwell) => (
        <DwellMarker
          key={String(dwell.id)}
          dwell={dwell}
          interactive={interactive}
          onSelect={onSelect}
        />
      ))}
    </>
  );
};

const DwellMarker: React.FC<{
  dwell: MissionDwell;
  interactive: boolean;
  onSelect?: (latitude: number, longitude: number) => void;
}> = ({ dwell, interactive, onSelect }) => {
  const color = dwell.own ? TEAL : GOLD;
  const clickable = !!onSelect && interactive;
  return (
    <Marker
      longitude={dwell.longitude}
      latitude={dwell.latitude}
      anchor="center"
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
    >
      <RichTooltip
        placement="top"
        eyebrow={dwell.own ? 'our kit' : 'observed'}
        eyebrowTone={color}
        body={<Box component="strong" sx={{ fontWeight: 700 }}>{dwell.place_name || dwell.place_address || dwell.label}</Box>}
        hint={[
          dwell.place_address && dwell.place_address !== dwell.place_name ? dwell.place_address : null,
          dwell.own && dwell.place_name ? dwell.label : null,
          dwell.duration_s != null ? formatSpan(dwell.duration_s) : null,
          dwell.start_at ? formatWhen(dwell.start_at) : null,
        ].filter(Boolean).join(' · ')}
      >
        <Box
          role={onSelect ? 'button' : undefined}
          tabIndex={clickable ? 0 : -1}
          aria-label={`${dwell.label}: ${dwell.duration_s != null ? formatSpan(dwell.duration_s) : 'stay'}`}
          onClick={onSelect ? () => onSelect(dwell.latitude, dwell.longitude) : undefined}
          onKeyDown={(e) => {
            if (!onSelect) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(dwell.latitude, dwell.longitude);
            }
          }}
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}2e`,
            border: `2px solid ${color}`,
            boxShadow: `0 0 6px ${color}88`,
            cursor: clickable ? 'pointer' : 'default',
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
        </Box>
      </RichTooltip>
    </Marker>
  );
};

export default MissionDwellLayer;
